/**
 * Dataset API functions.
 *
 * Maps to backend dataset-service endpoints:
 *   POST   /api/datasets/upload           - Upload CSV for validation (no storage)
 *   POST   /api/datasets                  - Create dataset metadata record
 *   GET    /api/datasets                  - List datasets (filterable)
 *   GET    /api/datasets/:id              - Get dataset metadata
 *   PUT    /api/datasets/:id              - Update dataset metadata
 *   DELETE /api/datasets/:id              - Delete dataset + values
 *   POST   /api/datasets/:id/values       - Upload time-series values
 *   GET    /api/datasets/:id/values       - Retrieve time-series values
 *   GET    /api/datasets/:id/values/stats - Get value statistics
 *   DELETE /api/datasets/:id/values       - Clear all values
 */

import client from './client';

// -------------------------------------------------------------------------
// CSV Upload / Validation
// -------------------------------------------------------------------------

/**
 * Upload and validate a CSV file (preview only, no storage).
 * @param {File} file - CSV file
 * @param {string} datasetType - Dataset type identifier
 * @returns {Promise<Object>} Validation report
 */
export const validateCSV = async (file, datasetType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('dataset_type', datasetType);

  return client.post('/api/datasets/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// -------------------------------------------------------------------------
// Dataset CRUD
// -------------------------------------------------------------------------

/**
 * Create a dataset metadata record.
 * @param {Object} data - { community_id, name, dataset_type, building_id?, metadata? }
 * @returns {Promise<Object>} Created dataset
 */
export const createDataset = async (data) => {
  return client.post('/api/datasets', data);
};

/**
 * List datasets with optional filters.
 * @param {Object} [params] - { community_id?, building_id?, dataset_type?, kind? }
 * @returns {Promise<Array>}
 */
export const getDatasets = async (params = {}) => {
  return client.get('/api/datasets', { params });
};

/**
 * Get a single dataset by ID.
 * @param {string} datasetId - UUID
 * @returns {Promise<Object>}
 */
export const getDataset = async (datasetId) => {
  return client.get(`/api/datasets/${datasetId}`);
};

/**
 * Update a dataset (partial).
 * @param {string} datasetId - UUID
 * @param {Object} data - Fields to update
 * @returns {Promise<Object>}
 */
export const updateDataset = async (datasetId, data) => {
  return client.put(`/api/datasets/${datasetId}`, data);
};

/**
 * Delete a dataset and all its values.
 * @param {string} datasetId - UUID
 * @returns {Promise<void>}
 */
export const deleteDataset = async (datasetId) => {
  return client.delete(`/api/datasets/${datasetId}`);
};

// -------------------------------------------------------------------------
// Time-series Values
// -------------------------------------------------------------------------

/**
 * Upload CSV data into the dataset_values hypertable.
 * @param {string} datasetId - UUID
 * @param {File} file - CSV file with time-series data
 * @returns {Promise<Object>} Insert summary
 */
export const uploadValues = async (datasetId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  return client.post(`/api/datasets/${datasetId}/values`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 minutes for large uploads
  });
};

/**
 * Retrieve time-series values for a dataset.
 * @param {string} datasetId - UUID
 * @param {Object} [params] - { start_time?, end_time?, limit?, offset? }
 * @returns {Promise<Object>} { dataset_id, values, total_count, ... }
 */
export const getValues = async (datasetId, params = {}) => {
  return client.get(`/api/datasets/${datasetId}/values`, { params });
};

/**
 * Get aggregate statistics for dataset values.
 * @param {string} datasetId - UUID
 * @returns {Promise<Object>} { count, min, max, mean, std, ... }
 */
export const getValuesStats = async (datasetId) => {
  return client.get(`/api/datasets/${datasetId}/values/stats`);
};

/**
 * Compute total annual load for a community by summing all building load datasets.
 * Returns null if no load datasets exist (caller should use default).
 * @param {string} communityId - Community UUID
 * @returns {Promise<number|null>} Total annual load in kWh, or null
 */
export const computeCommunityAnnualLoad = async (communityId) => {
  if (!communityId) return null
  try {
    const datasets = await getDatasets({ community_id: communityId })
    const loadDatasets = (Array.isArray(datasets) ? datasets : []).filter(
      d => d.kind === 'load' || (d.dataset_type && d.dataset_type.toLowerCase().includes('load'))
    )
    if (loadDatasets.length === 0) return null

    let totalAnnualLoad = 0
    for (const ds of loadDatasets) {
      try {
        const stats = await getValuesStats(ds.id)
        if (stats && stats.count > 0 && stats.mean != null) {
          // For hourly data: annual_load = sum = mean * count
          // For sub-hourly, this still gives total energy in kWh
          totalAnnualLoad += stats.mean * stats.count
        }
      } catch {
        // Skip datasets that fail stats (no values uploaded yet)
      }
    }
    return totalAnnualLoad > 0 ? Math.round(totalAnnualLoad) : null
  } catch {
    return null
  }
};

/**
 * Delete all time-series values for a dataset.
 * @param {string} datasetId - UUID
 * @returns {Promise<void>}
 */
export const deleteValues = async (datasetId) => {
  return client.delete(`/api/datasets/${datasetId}/values`);
};
